import React, { useState, useEffect, useMemo } from 'react';
import { collection, doc, getDocs, query, where, writeBatch, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { dekodaCp437, parsaSie, kontoGrupp, summeraPerGrupp } from '../sie';
import { BOLAG, GRUPP_LABELS } from './budgetModel';
import KostnadsAnalys from './KostnadsAnalys';

// ── BOKFÖRING (SIE-uppladdning) ───────────────────────────────────
// Ladda upp en SIE4-fil från Fortnox (eller annat program) → parsas helt
// i webbläsaren → månadssaldon per konto × kostnadsställe sparas i
// intel_bokforing (admin-only). Ger budgetassistenten historik som Qlik
// saknar: före dec 2024, övriga kostnader/avskrivningar/räntor, och
// Stigmyhrs huvudbutik. Fortnox-kopplingen (steg 2) fyller samma modell.

const GRUPPER = ['intakt', 'vk', 'ovrigt', 'personal', 'avskr', 'finans'];
const BOLAG_KORT = { hotell: 'Hotell AB', sc: 'SC AB', stigmyhrs: 'Stigmyhrs' };
const tkr = (v) => Math.round(v / 1000).toLocaleString('sv-SE');

function gissaBolag(foretag) {
  const f = (foretag || '').toLowerCase();
  if (f.includes('stigmyhr')) return 'stigmyhrs';
  if (f.includes(' sc') || f.includes('sportcenter')) return 'sc';
  return 'hotell';
}

export default function BokforingTab() {
  const [parsed, setParsed] = useState(null);   // resultat från parsaSie
  const [filnamn, setFilnamn] = useState('');
  const [bolag, setBolag] = useState('hotell');
  const [sparade, setSparade] = useState(null); // [{ym, summor, kalla}] för valt bolag
  const [sparar, setSparar] = useState(false);
  const [msg, setMsg] = useState('');
  const [fel, setFel] = useState('');

  // Sparade månader för valt bolag
  const laddaSparade = async (b) => {
    const snap = await getDocs(query(collection(db, 'intel_bokforing'), where('bolag', '==', b)));
    const ut = [];
    // Bara månadsdokument — collectionen rymmer även meta och kontotyper
    snap.forEach((d) => { const v = d.data(); if (v.ym && v.summor) ut.push(v); });
    ut.sort((a, b2) => b2.ym.localeCompare(a.ym));
    setSparade(ut);
  };
  useEffect(() => { setSparade(null); laddaSparade(bolag).catch(console.error); }, [bolag]);

  const lasFil = async (file) => {
    setFel(''); setMsg(''); setParsed(null);
    try {
      const buffer = await file.arrayBuffer();
      const p = parsaSie(dekodaCp437(buffer));
      if (!Object.keys(p.manader).length) {
        setFel('Inga transaktioner hittades — är det en SIE4-fil (typ 4, med verifikationer)? SIE typ 1–3 innehåller bara årssaldon.');
        return;
      }
      setParsed(p);
      setFilnamn(file.name);
      setBolag(gissaBolag(p.foretag));
    } catch (e) {
      setFel(`Kunde inte läsa filen: ${e.message}`);
    }
  };

  const perGrupp = useMemo(() => parsed ? summeraPerGrupp(parsed.manader) : null, [parsed]);
  const manaderSorterade = useMemo(() => perGrupp ? Object.keys(perGrupp).sort() : [], [perGrupp]);

  const spara = async () => {
    if (!parsed || sparar) return;
    setSparar(true); setFel(''); setMsg('');
    try {
      const email = auth.currentUser?.email || '';
      // Batch: ett dokument per månad + ett metadokument (konton/KS-namn).
      // Endast resultatkonton (grupp ≠ null) sparas — balanskonton behövs inte.
      const batch = writeBatch(db);
      let antalMan = 0;
      for (const [ym, rader] of Object.entries(parsed.manader)) {
        const radLista = [];
        for (const [nyckel, belopp] of Object.entries(rader)) {
          const [konto, ks] = nyckel.split('|');
          if (!kontoGrupp(konto)) continue;
          radLista.push({ konto, ks: ks || '', belopp });
        }
        if (!radLista.length) continue;
        batch.set(doc(db, 'intel_bokforing', `${bolag}_${ym}`), {
          bolag, ym,
          rader: radLista,
          summor: perGrupp[ym],
          kalla: filnamn,
          foretag: parsed.foretag,
          updatedAt: serverTimestamp(),
          updatedBy: email,
        });
        antalMan++;
      }
      // Metadokument: kontonamn + KS-namn (skrivs över, senaste filen vinner)
      const anvandaKonton = {};
      for (const rader of Object.values(parsed.manader)) {
        for (const nyckel of Object.keys(rader)) {
          const konto = nyckel.split('|')[0];
          if (kontoGrupp(konto) && parsed.konton[konto]) anvandaKonton[konto] = parsed.konton[konto];
        }
      }
      batch.set(doc(db, 'intel_bokforing', `${bolag}_meta`), {
        bolag, typ: 'meta', konton: anvandaKonton, ks: parsed.ks, updatedAt: serverTimestamp(), updatedBy: email,
      });
      await batch.commit();
      setMsg(`✓ ${antalMan} månader sparade för ${BOLAG.find((b) => b.id === bolag)?.namn} — assistenten kan nu använda dem som underlag`);
      setParsed(null); setFilnamn('');
      laddaSparade(bolag).catch(console.error);
    } catch (e) {
      setFel(`Kunde inte spara: ${e.message}`);
    } finally { setSparar(false); }
  };

  const chip = (active) => ({
    background: active ? '#44365C' : 'transparent', color: active ? '#fff' : 'var(--text-secondary)',
    border: '1px solid var(--border)', borderRadius: 20, padding: '4px 12px',
    fontSize: 11.5, cursor: 'pointer', fontFamily: 'system-ui,sans-serif',
  });
  const td = { fontSize: 11.5, padding: '3px 8px 3px 0', fontFamily: 'system-ui,sans-serif', textAlign: 'right', whiteSpace: 'nowrap' };
  const th = { ...td, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-secondary)' };
  const ymLabel = (ym) => `${ym.slice(0, 4)}-${ym.slice(4)}`;

  const summaTabell = (rader) => (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%' }}>
        <thead>
          <tr>
            <th style={{ ...th, textAlign: 'left' }}>Månad</th>
            {GRUPPER.map((g) => <th key={g} style={th}>{GRUPP_LABELS[g]}</th>)}
            <th style={th}>Resultat</th>
          </tr>
        </thead>
        <tbody>
          {rader.map(({ ym, summor, kalla }) => {
            const res = (summor.intakt || 0) - (summor.vk || 0) - (summor.ovrigt || 0) - (summor.personal || 0) - (summor.avskr || 0) - (summor.finans || 0);
            return (
              <tr key={ym} style={{ borderTop: '1px solid var(--border)' }} title={kalla ? `Källa: ${kalla}` : undefined}>
                <td style={{ ...td, textAlign: 'left', fontWeight: 600 }}>{ymLabel(ym)}</td>
                {GRUPPER.map((g) => <td key={g} style={td}>{summor[g] ? tkr(summor[g]) : '–'}</td>)}
                <td style={{ ...td, fontWeight: 700, color: res >= 0 ? '#2E7D32' : '#A32D2D' }}>{tkr(res)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      <div className="card">
        <div className="card-title">📚 Bokföring — SIE-uppladdning</div>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif', marginBottom: 10, lineHeight: 1.5 }}>
          Exportera en <strong>SIE4-fil</strong> från Fortnox (Register → Exportera → SIE, typ 4 med verifikationer) och ladda upp den här.
          Månadssaldon per konto och kostnadsställe sparas i portalen och blir underlag för budgetassistenten —
          även för det Qlik saknar: tiden före december 2024, övriga kostnader, avskrivningar, räntor och Stigmyhrs huvudbutik.
        </div>
        <input type="file" accept=".se,.si,.sie,.SE,.txt" onChange={(e) => e.target.files[0] && lasFil(e.target.files[0])}
          style={{ fontSize: 12.5, fontFamily: 'system-ui,sans-serif', marginBottom: 6 }} />
        {fel && <div style={{ fontSize: 12, color: '#A32D2D', fontFamily: 'system-ui,sans-serif', marginTop: 6 }}>{fel}</div>}
        {msg && <div style={{ fontSize: 12, color: '#2E7D32', fontFamily: 'system-ui,sans-serif', marginTop: 6 }}>{msg}</div>}
      </div>

      {parsed && perGrupp && (
        <div className="card" style={{ border: '2px solid #44365C' }}>
          <div className="card-title">Förhandsvisning — {parsed.foretag || filnamn}</div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif', marginBottom: 8 }}>
            {parsed.verCount.toLocaleString('sv-SE')} verifikationer · {parsed.transCount.toLocaleString('sv-SE')} transaktionsrader ·{' '}
            {manaderSorterade.length} månader ({ymLabel(manaderSorterade[0])} – {ymLabel(manaderSorterade[manaderSorterade.length - 1])})
            {parsed.fel.length > 0 && <span style={{ color: '#A36B00' }}> · {parsed.fel.length} rader kunde inte tolkas</span>}
          </div>

          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10, fontFamily: 'system-ui,sans-serif' }}>
            <span style={{ fontSize: 12 }}>Spara som bolag:</span>
            {BOLAG.map((b) => (
              <button key={b.id} onClick={() => setBolag(b.id)} style={chip(bolag === b.id)}>{b.namn}</button>
            ))}
          </div>

          {summaTabell(manaderSorterade.map((ym) => ({ ym, summor: perGrupp[ym] })))}

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
            <button onClick={spara} disabled={sparar}
              style={{ background: '#44365C', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 16px', fontSize: 12.5, cursor: 'pointer', fontFamily: 'system-ui,sans-serif', opacity: sparar ? 0.6 : 1 }}>
              {sparar ? 'Sparar…' : `✓ Spara ${manaderSorterade.length} månader`}
            </button>
          </div>
          <div className="note">
            Tkr, resultaträkningens tecken (intäkter och kostnader positiva). Stäm av ett par månader mot Fortnox innan du sparar.
            Befintliga månader för samma bolag skrivs över — att ladda upp en nyare export är alltså säkert.
          </div>
        </div>
      )}

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
          <div className="card-title" style={{ marginBottom: 0 }}>Sparad bokföring</div>
          <div style={{ display: 'flex', gap: 5 }}>
            {BOLAG.map((b) => (
              <button key={b.id} onClick={() => setBolag(b.id)} style={chip(bolag === b.id)} title={b.namn}>{BOLAG_KORT[b.id] || b.namn}</button>
            ))}
          </div>
        </div>
        {sparade === null && <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>Hämtar…</div>}
        {sparade !== null && !sparade.length && <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>Ingen bokföring uppladdad för det här bolaget än.</div>}
        {sparade !== null && sparade.length > 0 && summaTabell(sparade)}
      </div>

      {/* Kostnadsanalys — fast vs rörligt per konto, för valt bolag */}
      <KostnadsAnalys bolag={bolag} />
    </>
  );
}
